import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import {
  NoticeSearchResponse,
  NoticeService,
  NoticeType,
  Step,
  User,
  TaxYearService,
  ClientService
} from "../../../../swaggerServices";
import { Subject, Subscription } from "rxjs";
import { makeName } from "../../../shared/util/makeName";
import { sortBy } from "lodash-es";
import { INotice, SearchFilters } from "../types";
import { asNumber, constructNotices, formatCurrency } from "../util";
import { NoticesStateService } from "../notices-state.service";
import { ConfigStateService } from '../../config/config-state.service';
import { debounceTime } from "rxjs/operators";
@Component({
    selector: "app-notice-table",
    templateUrl: "./notice-table.component.html",
    styleUrls: ["./notice-table.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NoticeTableComponent implements OnInit, OnDestroy {
  @Input()
  selectedNotice?: INotice;
  @Output()
  noticeSelected: EventEmitter<INotice> = new EventEmitter<INotice>();
  @Output()
  reprocessNotices: EventEmitter<INotice[]> = new EventEmitter<INotice[]>();

  subscriptions: Subscription[] = [];
  searchSub?: Subscription;
  notices: INotice[];
  noticeTypes: NoticeType[];
  steps: Step[];
  users: User[];
  loading = false;
  userUpdateSub?: Subscription;
  noticeYears = [];
  clientList = [];
  filters: SearchFilters = {
    exceptions: "Yes",
    atlasId: "",
    name: "",
    step: "Any",
    noticeType: "Any",
    user: "Any",
    year: null,
    receiveDate: "",
    clientId: null,
    noticeTypeGroup: ""
  };
  showFilters = true;

  searchChanged: Subject<void> = new Subject<void>();

  pendingOwnerUpdates: { [noticeId: string]: User | null } = {}; // map from noticeId to new userId
  pendingAssigneeUpdates: { [noticeId: string]: User | null } = {}; // as above

  noticeTypeGroupList = [
    { value: "AS", label: "PAYGs" },
    { value: "EX", label: "Exception" },
    { value: "NOA", label: "NOAs" },
    { value: "ST", label: "Save to Docshare" },
    { value: "GE", label: "Straight through" }
  ];
  // Keep track of owner and assigned-to dropdowns on a per-notice basis.
  // When the value changes, submit a request to the backend and show a loader.

  constructor(
    private cdr: ChangeDetectorRef,
    private noticeService: NoticeService,
    private noticesState: NoticesStateService,
    private clientService: ClientService,
    private config: ConfigStateService
  ) { }

  ngOnInit() {
    this.subscriptions.push(this.config.loadConfig().subscribe(() => { }));

    this.subscriptions.push(this.noticesState.searchRequested.subscribe(() => {
      this.clearSelectedNoticeAndSearch();
    }));

    this.subscriptions.push(this.loadClientList());
    const s1 = this.searchChanged.pipe(debounceTime(400)).subscribe(r => {
      this.doSearch();
    });
    this.subscriptions.push(s1);
    // instant search on load for the first search
    this.doSearch();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
    if (this.searchSub) {
      this.searchSub.unsubscribe();
    }
  }

  triggerSearch() {
    this.noticesState.selectNotice(null);
    this.loading = true;
    if (this.searchSub) {
      this.searchSub.unsubscribe();
    }
    this.searchChanged.next();
  }

  filterExceptionsChanged(option: "Any" | "Yes" | "No") {
    this.filters.exceptions = option;
    this.triggerSearch();
  }

  filterAtlasIdChanged(atlasId: string) {
    this.filters.atlasId = atlasId;
    this.triggerSearch();
  }

  filterNameChanged(name: string) {
    this.filters.name = name;
    this.triggerSearch();
  }

  filterStepChanged(step: string) {
    this.filters.step = step;
    this.triggerSearch();
  }

  filterNoticeTypeChanged(noticeType: string) {
    this.filters.noticeType = noticeType;
    this.triggerSearch();
  }

  filterUserChanged(user: string) {
    this.filters.user = user;
    this.triggerSearch();
  }

  filterYearChanged(year) {
    this.filters.year = year;
    this.triggerSearch();
  }

  filterClientChanged(clientId) {
    this.filters.clientId = clientId;
    this.triggerSearch();
  }

  filterReceiveDateChanged(receiveDate) {
    this.triggerSearch();
  }

  filterNoticeTypeGroupChanged(event) {
    this.filters.noticeTypeGroup = event;
    if (!event || event === "") {
      this.filters.noticeTypeGroup = null;
    }
    this.triggerSearch();
  }

  doSearch() {
    this.loading = true;
    if (this.searchSub) {
      this.searchSub.unsubscribe();
    }
    const {
      atlasId,
      name,
      exceptions,
      step,
      user,
      noticeType,
      year,
      receiveDate
    } = this.filters;
    const hasException =
      exceptions === "Yes" ? true : exceptions === "No" ? false : null;

    this.searchSub = this.noticeService
      .searchNotices(
        hasException,
        atlasId ? asNumber(atlasId) : null,
        name || "",
        step ? asNumber(step) : null,
        user === "Any"
          ? undefined
          : user === "Unassigned"
            ? null
            : asNumber(user),
        noticeType ? asNumber(noticeType) : null,
        year ? asNumber(year) : null,
        receiveDate ? receiveDate : null,
        this.filters.clientId ? asNumber(this.filters.clientId) : null,
        this.filters.noticeTypeGroup ? this.filters.noticeTypeGroup : null
      )
      .subscribe(
        (resp: NoticeSearchResponse) => {
          const selectedNoticeIds = new Set(
            this.getSelectedNotices().map(n => n.id)
          );
          this.loading = false;

          const fullNotices = constructNotices(resp);
          fullNotices.forEach(n => {
            if (selectedNoticeIds.has(n.id)) {
              n.checkboxSelected = true;
            }
          });
          this.notices = fullNotices;
          this.noticeTypes = resp.noticeTypes;
          this.steps = resp.steps;
          this.noticeYears = resp.years;
          this.users = sortBy(resp.users, ["firstName", "lastName"]);
          this.noticesState.setUsers(this.users);
          this.cdr.detectChanges();
        },
        e => {
          this.loading = false;
          this.cdr.detectChanges();
        }
      );
  }

  makeName(user?: User) {
    return user ? makeName(user) : "";
  }

  getOwner(n: INotice) {
    const u = this.pendingOwnerUpdates[n.id];
    return u !== undefined ? u : n.ownerUser;
  }
  getOwnerName(n: INotice) {
    const u = this.getOwner(n);
    return u ? this.makeName(u) : "";
  }

  getAssignee(n: INotice) {
    const u = this.pendingAssigneeUpdates[n.id];
    // console.log('getAssignee', u, n.assignedUser)
    return u !== undefined ? u : n.assignedUser;
  }
  getAssigneeName(n: INotice) {
    const u = this.getAssignee(n);
    return u ? this.makeName(u) : "";
  }

  ownerChanged(n: INotice, newOwnerId?: string) {
    const id = newOwnerId ? Number(newOwnerId) : null;
    const u = this.users.find(u => u.id === id);
    this.pendingOwnerUpdates[n.id] = u;
    this.triggerUserUpdate();
    this.cdr.detectChanges();
  }

  assigneeChanged(n: INotice, newAssigneeId?: string) {
    const id = newAssigneeId ? Number(newAssigneeId) : null;
    const u = this.users.find(u => u.id === id);
    this.pendingAssigneeUpdates[n.id] = u;
    this.triggerUserUpdate();
    this.cdr.detectChanges();
  }

  triggerUserUpdate() {
    if (!this.userUpdateSub) {
      const ownerKeys = Object.keys(this.pendingOwnerUpdates);
      const assigneeKeys = Object.keys(this.pendingAssigneeUpdates);
      if (ownerKeys.length) {
        const noticeId: string = ownerKeys[0];
        const newOwner: User | null = this.pendingOwnerUpdates[noticeId];
        const newOwnerId = newOwner ? newOwner.id : null;
        this.userUpdateSub = this.noticeService
          .setOwner(Number(noticeId), { userId: newOwnerId })
          .subscribe(
            n => {
              this.notices = this.notices.map(nn => {
                if (nn.id === n.id) {
                  return {
                    ...nn,
                    ownerUserId: n.ownerUserId,
                    ownerUser: newOwner
                  };
                }
                return nn;
              });
              // if the pending update is still the same, remove it
              // if there is another pending update, don't remove it
              if (this.pendingOwnerUpdates[n.id] === newOwner) {
                delete this.pendingOwnerUpdates[n.id];
              }
              this.userUpdateSub = null;
              this.triggerUserUpdate();
              this.cdr.detectChanges();
            },
            e => {
              delete this.pendingOwnerUpdates[noticeId];
              this.userUpdateSub = null;
              this.triggerUserUpdate();
              this.cdr.detectChanges();
            }
          );
      } else if (assigneeKeys.length) {
        const noticeId: string = assigneeKeys[0];
        const newAssignee: User | null = this.pendingAssigneeUpdates[noticeId];
        const newAssigneeId = newAssignee ? newAssignee.id : null;
        this.userUpdateSub = this.noticeService
          .setAssignedTo(Number(noticeId), { userId: newAssigneeId })
          .subscribe(
            n => {
              this.notices = this.notices.map(nn => {
                if (nn.id === n.id) {
                  return {
                    ...nn,
                    assignedUserId: n.assignedUserId,
                    assignedUser: newAssignee
                  };
                }
                return nn;
              });

              // if the pending update is still the same, remove it
              // if there is another pending update, don't remove it
              if (this.pendingAssigneeUpdates[noticeId] === newAssignee) {
                delete this.pendingAssigneeUpdates[noticeId];
              }
              this.userUpdateSub = null;
              this.triggerUserUpdate();
              this.cdr.detectChanges();
            },
            e => {
              delete this.pendingAssigneeUpdates[noticeId];
              this.userUpdateSub = null;
              this.triggerUserUpdate();
              this.cdr.detectChanges();
            }
          );
      }
    }
  }

  noticeTrackBy(index: number, n: INotice) {
    return n.id;
  }

  getAmountStr(amount?: number) {
    return formatCurrency(amount);
  }

  setNoticeCheckboxSelected(n: INotice, selected: boolean) {
    // this.fullNotices = this.fullNotices.map(nn => nn === n ? {...nn, checkboxSelected: selected } : nn );
    n.checkboxSelected = selected;
    this.cdr.detectChanges();
  }
  getSelectedNotices() {
    return this.notices ? this.notices.filter(n => n.checkboxSelected) : [];
  }

  handleReprocessingSubmitted(notices: INotice[]) {
    const ids = new Set(notices.map(n => n.id));
    this.notices.forEach(n => {
      if (ids.has(n.id)) {
        n.checkboxSelected = false;
      }
    });
    this.clearSelectedNoticeAndSearch();
  }

  handleContinueProcessingSubmitted(notice: INotice) {
    this.handleReprocessingSubmitted([notice]);
  }

  clearSelectedNoticeAndSearch() {
    this.noticesState.selectNotice(null);
    this.doSearch();
  }

  private loadClientList() {
    return this.clientService.getClientsWithNotices().subscribe(clients => {
      this.clientList = clients.map((c, i) => {
        return {
          id: c.id,
          name: c.name
        };
      });
    });
  }
}
